import { ICliente } from '@app/modules/clientes/model/cliente.model';
import { IEquipeVenda } from '@app/modules/equipe-venda/model/equipe-venda.model';
import { IMunicipio } from '@app/modules/parametrizacao/municipio/model/municipio.model';
import {
  IParceiro,
  Parceiro
} from '@app/modules/parceiro/model/parceiro.model';
import { isLastDayOfMonth } from '@app/shared/utils';
import * as moment from 'moment';
import { IEntity, LiteralObject, User } from 'viggo-sistemas-core-web';

export interface Agenda{
  date: string;
  visitas_c: string[];
  visitas_v: string[];
  visitou_c: string[];
  visitou_v: string[];
  date_: Date;
}

export interface EventoPerdeGanha{
  novo_valor: number;
  numero_entidade: string;
  entidade_id: string;
  ajuste_direto: boolean;
}

export interface EventoPerdeGanhaResult{
  active: boolean;
  descricao: string;
  entidade_id: string;
  id: string;
  saldo: number;
  saldo_pos: number;
  saldo_pre: number;
  seq: number;
  status: string;
  tipo: string;
}


/**
 * Frequência de visitas possiveis de um vendedor a um cliente
 */
export enum FREQUENCIA_VISITAS {
  DIARIA = 'DIARIA',
  SEMANAL = 'SEMANAL',
  QUINZENAL = 'QUINZENAL',
  MENSAL = 'MENSAL',
  SEM_FREQUENCIA = 'NONE'
}

/**
 * Interface que representa uma carteira de clientes para
 * um vendedor.
 * Como opções obrigatórias, para enviar uma cliente que
 * já está na carteira do vendedor
 */
export interface IClienteCarteiraVendedor {
  id: string;
  cliente_id: string;
  data_inicio: Date;
  frequencia: FREQUENCIA_VISITAS;
  cliente?: ICliente;
}

/**
 * Interface que representa um vendedor dentro
 * do back-end
 */
export interface IVendedor extends IEntity {
  /**
   * Id do dominio
   */
  domain_id: string;
  data_nascimento: Date | string;
  // representa uma lista de clientes para este vendedor
  carteira: IClienteCarteiraVendedor[];
  // representa a agenda do vendedor
  agenda_value?: { agenda: Agenda[], totVisitas: number, totVisitados: number };
  /**
   * Usuário que libera o acesso a este vendedor
   */
  codigo?: number;
  user?: User;
  /**
   * Id deste usuario
   */
  user_id?: string;

  /**
   * Id deste usuario
   */
  parceiro?: IParceiro;

  equipe_venda?: IEquipeVenda;
  equipe_venda_id?: string;

  saldo_perde_ganha?: number;

  settings?: LiteralObject;

  settings_domain?: LiteralObject[];

  padrao_catalogo?: boolean;
}
/**
 * Modelo que representa o filtro de um usuário
 */
export interface IFilterClients {
  cidades: IMunicipio[];
}

/**
 * Interface simples para tipar as visitas
 */
export interface IVisitaCliente {
  data_inicial: Date;
  nome_cliente: string;
  cliente_id: string;
  frequencia: FREQUENCIA_VISITAS;
  visitado?: boolean;
}

export class Vendedor extends Parceiro implements IVendedor {
  agenda_value: { agenda: Agenda[], totVisitas: number, totVisitados: number };
  data_nascimento: string | Date;
  carteira: IClienteCarteiraVendedor[];

  constructor(valoresIniciais: IVendedor = {} as IVendedor) {
    super();
    Object.assign(this, valoresIniciais);
    if (valoresIniciais) {
      this.carteira = this.carteira.map(cliente => ({
        ...cliente,
        data_inicio: cliente.data_inicio ? new Date(cliente.data_inicio) : null
      }));
    }
  }
  /**
   * Encontros diarias para visita com base em uma data
   * @param dataCalendario data a ser testada
   * @returns {IVisitaCliente[]} array com nome_razao_social dos clientes com visitas agendadas
   */
  public quantidadeVisitasDiarias(dataCalendario: Date): IVisitaCliente[] {
    const clientes: IVisitaCliente[] = [];
    this.carteira.forEach(c => {
      if (
        c.frequencia === FREQUENCIA_VISITAS.DIARIA &&
        c.data_inicio <= dataCalendario
      ) {
        clientes.push({
          nome_cliente: c.cliente.parceiro.nome_razao_social,
          data_inicial: c.data_inicio,
          frequencia: FREQUENCIA_VISITAS.DIARIA,
          cliente_id: c.cliente.id
        });
      }
    });
    return clientes;
  }
  /**
   * Pesquisa na carteira de clientes, visitas semanais agendadas para uma data repassada
   * @param dataCalendario data a ser testada
   * @returns {IVisitaCliente[]} array com nome_razao_social dos clientes agendados
   */
  public quantidadeVisitasSemanais(dataCalendario: Date): IVisitaCliente[] {
    const clientes: IVisitaCliente[] = [];

    this.carteira.forEach(visita => {
      if (
        visita.frequencia === FREQUENCIA_VISITAS.SEMANAL &&
        visita.data_inicio <= dataCalendario
      ) {
        if (dataCalendario.getDay() === visita.data_inicio.getDay()) {
          clientes.push({
            nome_cliente: visita.cliente.parceiro.nome_razao_social,
            data_inicial: visita.data_inicio,
            frequencia: FREQUENCIA_VISITAS.SEMANAL,
            cliente_id: visita.cliente.id
          });
        }
      }
    });

    return clientes;
  }
  /**
   * Pesquida na carteira de clientes, visitas quinzenais agendadas para uma data repassada
   * @param dataCalendario data a ser tesdada
   */
  public quantidadeVisitasQuinzena(dataCalendario: Date): IVisitaCliente[] {
    const clientes: IVisitaCliente[] = [];

    this.carteira.forEach(visitaCliente => {
      if (
        visitaCliente.frequencia === FREQUENCIA_VISITAS.QUINZENAL &&
        visitaCliente.data_inicio <= dataCalendario
      ) {
        const diferenca = moment(dataCalendario).diff(
          moment(visitaCliente.data_inicio),
          'days'
        );

        if (
          dataCalendario.getDay() === visitaCliente.data_inicio.getDay() &&
          diferenca % 14 === 0
        ) {
          clientes.push({
            nome_cliente: visitaCliente.cliente.parceiro.nome_razao_social,
            data_inicial: visitaCliente.data_inicio,
            frequencia: FREQUENCIA_VISITAS.QUINZENAL,
            cliente_id: visitaCliente.cliente.id
          });
        }
      }
    });

    return clientes;
  }
  /**
   * Retorna os encontros mensais para uma data
   * @param dataCalendario data a ser pesquisada
   */
  public quantidadeVisitasMensais(dataCalendario: Date): IVisitaCliente[] {
    const clientes: IVisitaCliente[] = [];

    this.carteira.forEach(carteiraCliente => {
      const { data_inicio: data_inicial } = carteiraCliente;

      if (
        carteiraCliente.frequencia === FREQUENCIA_VISITAS.MENSAL &&
        data_inicial <= dataCalendario
      ) {
        if (
          dataCalendario.getDate() === data_inicial.getDate() ||
          (isLastDayOfMonth(dataCalendario) &&
            dataCalendario.getDate() <= data_inicial.getDate())
        ) {
          clientes.push({
            nome_cliente: carteiraCliente.cliente.parceiro.nome_razao_social,
            data_inicial: carteiraCliente.data_inicio,
            frequencia: FREQUENCIA_VISITAS.MENSAL,
            cliente_id: carteiraCliente.cliente.id
          });
        }
      }
    });

    return clientes;
  }
  /**
   * Prepara a entidade para ser enviada para o backend
   * @returns {IVendedor} entitdade tratada
   */
  preparaEntity(): IVendedor {
    const vendedor: IVendedor = { ...this };

    vendedor.user_id = vendedor.user ? vendedor.user.id : undefined;

    vendedor.user = undefined;

    vendedor.carteira = vendedor.carteira.map(clienteCarteira => {
      const value: IClienteCarteiraVendedor = {
        ...clienteCarteira
      } as IClienteCarteiraVendedor;

      value.id = clienteCarteira.id;
      value.cliente_id = value.cliente ? value.cliente.id : value.cliente_id;
      value.cliente = undefined;
      return value;
    });

    vendedor.parceiro.enderecos = vendedor.parceiro.enderecos.map(endereco => ({
      ...endereco,
      municipio_id: endereco.municipio
        ? endereco.municipio.id
        : endereco.municipio_id,
      municipio: undefined
    }));

    delete vendedor.settings;
    delete vendedor.settings_domain;
    delete vendedor.parceiro;
    delete vendedor.agenda_value;
    return vendedor;
  }
  /**
   * Altera um cliente na carteira de cliente
   * @param cliente cliente a ser alterado
   */
  public setElementoCarteiraCliente(cliente: IClienteCarteiraVendedor): void {
    this.carteira = this.carteira.map(c => {
      if (c.cliente.parceiro.cpf_cnpj === cliente.cliente.parceiro.cpf_cnpj)
        return cliente;
      return c;
    });
  }
  /**
   * Reseta as visitas para os ids repassados
   * @param ids ids dos clientes que terão encontros removidos
   */
  public resetVisita(...ids: string[]) {
    this.carteira.forEach(value => {
      if (ids.includes(value.cliente.id)) {
        value.cliente_id = value.cliente.id;
        value.data_inicio = null;
        value.frequencia = FREQUENCIA_VISITAS.SEM_FREQUENCIA;
      }
    });
  }
}
